import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess } from '@/types/api';
import type { ImportResult } from '@/types/institute';
import type { CollegeAdmissionPayload } from '../schemas/collegeAdmission.schema';
import type { DocumentKind } from '../constants';
import type { AdmissionPayload } from '../schemas/admission.schema';

/**
 * Every call the admissions screen makes.
 *
 * As with the rest of the institute area there is no institute id in the path — the
 * server takes it from the caller's token, so there is nothing for a client to
 * point at another institute's students. The academic year is the server's
 * decision too, and so is the admission number: it comes from the institute's own
 * series (`admissionNumberPrefix`, `admissionNumberNext` in settings), which is
 * why the form never asks anyone to type one.
 */

/**
 * Whether the new student can sign in to the student panel yet.
 *
 * `INVITED` — an account was opened and a link to set a password is on its way.
 * `NO_EMAIL` — no address of their own, so there was nowhere to send one.
 * `EMAIL_TAKEN` — somebody at this institute already signs in with it.
 */
export type StudentAccountOutcome = 'INVITED' | 'NO_EMAIL' | 'EMAIL_TAKEN';

/**
 * Whether the new student's parent can sign in to the *parent* panel yet.
 *
 * A separate account from the child's, so a separate outcome: for a young child
 * this is the login the family actually uses.
 *
 * `INVITED` — an account was opened and a link to set a password is on its way.
 * `LINKED` — the parent already signs in here, and this child joined that
 *   account rather than opening a second one; no link is needed.
 * `NO_EMAIL` — no parent on the record has an address, so there was nowhere to
 *   send one.
 * `EMAIL_TAKEN` — somebody else at this institute already signs in with it.
 */
export type ParentAccountOutcome = 'INVITED' | 'LINKED' | 'NO_EMAIL' | 'EMAIL_TAKEN';

/** What a registered student comes back as. */
export interface AdmittedStudent {
  uuid: string;
  admissionNumber: string;
  fullName: string;
  className: string;
  sectionName: string;
  account: StudentAccountOutcome;
  parentAccount: ParentAccountOutcome;
}

export type AdmissionDocuments = Partial<Record<DocumentKind, File>>;

/**
 * Multipart rather than JSON because the certificates travel with the record:
 * an admission that half-saved — student written, birth certificate lost — is
 * worse than one that failed outright. The server puts the files in S3 under
 * its own generated keys and stores only those keys (CLAUDE.md §11).
 */
function toFormData(
  payload: AdmissionPayload,
  photo: File | null,
  documents: AdmissionDocuments,
): FormData {
  const body = new FormData();
  body.append('payload', JSON.stringify(payload));
  if (photo) body.append('photo', photo);

  for (const [kind, file] of Object.entries(documents)) {
    if (file) body.append(`documents[${kind}]`, file);
  }

  return body;
}

export interface ImportSheetOptions {
  confirmSharedMobiles?: boolean;
}

export const admissionsApi = {
  create: (payload: AdmissionPayload, photo: File | null, documents: AdmissionDocuments) =>
    unwrap(
      apiClient.post<ApiSuccess<AdmittedStudent>>(
        '/institute/admissions',
        toFormData(payload, photo, documents),
        // Left to the browser deliberately: it appends the multipart boundary,
        // which a hand-written Content-Type header would drop.
        { headers: { 'Content-Type': undefined } },
      ),
    ),

  /**
   * Admitting a college student.
   *
   * A plain JSON body, unlike the institute route: a college admits on basic
   * details, so there are no certificates travelling with the record. Its own
   * method rather than a flag on `create`, because the two send different
   * shapes to different endpoints and the server refuses each at the wrong
   * kind of institute.
   */
  createForCollege: (payload: CollegeAdmissionPayload) =>
    unwrap(apiClient.post<ApiSuccess<AdmittedStudent>>('/institute/admissions/college', payload)),

  /** Where the blank template lives. Fetched with the token, like every upload. */
  templatePath: () => '/institute/admissions/import/template',

  /**
   * Admitting many students from one spreadsheet.
   *
   * Multipart with a single `sheet` field. The Content-Type is left to the
   * browser so it appends the multipart boundary, which a hand-written header
   * would drop.
   */
  /**
   * `confirmSharedMobiles` is sent once the office has checked the parent
   * mobiles the first upload found shared between students.
   */
  importSheet: (sheet: File, options: ImportSheetOptions = {}) => {
    const form = new FormData();
    form.append('sheet', sheet);
    if (options.confirmSharedMobiles) form.append('confirmSharedMobiles', 'true');

    return unwrap(
      apiClient.post<ApiSuccess<ImportResult>>('/institute/admissions/import', form, {
        headers: { 'Content-Type': undefined },
      }),
    );
  },
};
